import { deviceApi } from "#/api/deviceApi";
import { shipmentApi } from "#/api/shipmentApi";
import { vehicleApi } from "#/api/vehicleApi";
import ActionButton from "#/components/buttons/ActionButton";
import {
  showErrorNotification,
  showSuccessNotification,
} from "#/components/notifications";
import DetailPageLayout from "#/components/ui/DetailPageLayout";
import type { UpdateShipmentPayload } from "#/types/shipment";
import { getRandomColor } from "#/utils/ColorUtils";
import {
  Accordion,
  Badge,
  Button,
  Select,
  Table,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { IconEdit, IconTrash } from "@tabler/icons-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useClient } from "#/hook/useClient";
import { formatDate, formatDateTime } from "#/utils/dateTimeUtils";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { usePermission } from "#/hooks/usePermission";
import PermissionTooltip from "#/components/ui/PermissionTooltip";

const ShipmentLogDetailPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { client } = useClient();
  const { canWrite } = usePermission();
  const [isEditMode, setIsEditMode] = useState(false);
  const [itemsToRemove, setItemsToRemove] = useState<string[]>([]);
  const [
    deleteModalOpened,
    { open: openDeleteModal, close: closeDeleteModal },
  ] = useDisclosure(false);
  const [itemToDelete, setItemToDelete] = useState<{
    tag_id: string;
    id: number;
  }>();

  const form = useForm<{
    label: string;
    vehicle_rfid: string;
    scanner_id: number | undefined;
    location: string;
    pallet_id: string;
  }>({
    initialValues: {
      label: "",
      vehicle_rfid: "",
      scanner_id: undefined,
      location: "",
      pallet_id: "",
    },
  });

  const handleSubmit = (values: typeof form.values) => {
    const payload: Record<string, unknown> = {
      label: values.label,
      vehicle_rfid: values.vehicle_rfid,
      location: values.location,
      pallet_id: values.pallet_id,
    };

    if (values.scanner_id) {
      payload.scanner_id = values.scanner_id;
    }

    // If there are items to remove, include the updated items list
    if (itemsToRemove.length > 0 && shipment) {
      const remainingItems = shipment.items
        .filter((item) => !itemsToRemove.includes(item.tag_id))
        .map((item) => item.tag_id);
      payload.items = remainingItems;
    }

    updateMutation.mutate(payload);
  };

  const updateMutation = useMutation({
    mutationFn: async (payload: UpdateShipmentPayload) => {
      if (!id) throw new Error("Invalid shipment ID");
      return await shipmentApi.update(Number(id), payload);
    },
    onSuccess: () => {
      showSuccessNotification({
        message: t("shipments.updatedSuccess"),
      });
      setItemsToRemove([]);
      setIsEditMode(false);
      form.resetDirty();
      void getShipment();
    },
    onError: (error) => {
      console.log("🚀 ~ updateShipment ~ error:", error);
      showErrorNotification({
        message: error.message ?? t("shipments.updateFailed"),
      });
    },
  });

  const {
    data: shipment,
    isPending,
    refetch: getShipment,
  } = useQuery({
    queryKey: ["shipmentDetails", id],
    enabled: !!id,
    queryFn: async () => {
      if (!id) throw new Error("Invalid shipmentId");

      const response = await shipmentApi.getById(Number(id));
      if (!response.data) throw new Error("Failed to fetch shipment");

      const shipment = response.data;
      console.log("Shipment vehicle RFID:", shipment.vehicle?.rfid_tag?.rfid);
      form.setValues({
        label: shipment.label ?? "",
        vehicle_rfid: shipment.vehicle?.rfid_tag?.rfid ?? "",
        scanner_id: shipment.scanner?.id,
        location: shipment.location ?? "",
        pallet_id: shipment.pallet_id ?? "",
      });
      form.resetDirty();
      console.log("Form values after setting:", form.values);

      return shipment;
    },
  });

  const { data: { vehicles = [] } = {}, isLoading: vehiclesLoading } = useQuery(
    {
      queryKey: ["vehicles-all"],
      queryFn: async () => {
        const response = await vehicleApi.getAll({
          page: 1,
          limit: 10000,
        });
        if (!response.data) {
          throw new Error("Failed to fetch vehicles");
        }
        console.log("Loaded vehicles:", response.data.data);
        return {
          vehicles: response.data.data,
        };
      },
      enabled: true,
      staleTime: 1000 * 60 * 5,
    }
  );

  const { data: { devices = [] } = {} } = useQuery({
    queryKey: ["devices-all"],
    queryFn: async () => {
      const response = await deviceApi.getAll({
        page: 1,
        limit: 10000,
      });
      if (!response.data) {
        throw new Error("Failed to fetch devices");
      }
      return {
        devices: response.data.data,
      };
    },
    staleTime: 1000 * 60 * 5,
  });

  const handleRemoveItem = (tag_id: string) => {
    setItemsToRemove((prev) => [...prev, tag_id]);
    form.setDirty({ label: true });
    closeDeleteModal();
  };

  const handleCancelEdit = () => {
    form.reset();
    setItemsToRemove([]);
    setIsEditMode(false);
  };

  const displayedItems =
    shipment?.items?.filter((item) => !itemsToRemove.includes(item.tag_id)) ??
    [];

  return (
    <DetailPageLayout
      title={shipment?.label ?? t("shipments.shipmentDetails")}
      loading={isPending}
      navRightSection={
        <div className="w-full flex justify-end gap-2 items-center">
          {!isEditMode ? (
            <PermissionTooltip disabled={!canWrite()}>
              <Button
                color="black"
                variant="filled"
                leftSection={<IconEdit size={16} />}
                onClick={() => setIsEditMode(true)}
                size="xs"
                disabled={!canWrite()}
              >
                {t("common.edit")}
              </Button>
            </PermissionTooltip>
          ) : (
            <>
              <Button
                color="black"
                variant="outline"
                onClick={handleCancelEdit}
                size="xs"
              >
                {t("common.cancel")}
              </Button>

              <PermissionTooltip disabled={!canWrite()}>
                <Button
                  color="black"
                  variant="filled"
                  loading={updateMutation.isPending}
                  disabled={(!form.isDirty() && itemsToRemove.length === 0) || !canWrite()}
                  onClick={() => {
                    void handleSubmit(form.values);
                  }}
                  size="xs"
                >
                  {t("common.save")}
                </Button>
              </PermissionTooltip>
            </>
          )}
        </div>
      }
    >
      <div>
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Accordion
            variant="separated"
            defaultValue={["details", "items"]}
            multiple
          >
            <Accordion.Item value="details">
              <Accordion.Control>{t("shipments.shipmentDetails")}</Accordion.Control>
              <Accordion.Panel>
                <div className="w-full flex flex-col gap-4">
                  <div className="w-full grid grid-cols-2 gap-4">
                    {isEditMode ? (
                      <TextInput
                        label={t("shipments.form.label")}
                        withAsterisk
                        placeholder={t("shipments.form.labelPlaceholder")}
                        {...form.getInputProps("label")}
                      />
                    ) : (
                      <div className="flex flex-col gap-1">
                        <span className="text-sm font-semibold text-slate-600">
                          {t("shipments.form.label")}
                        </span>
                        <span className="text-sm">
                          {shipment?.label ?? "--"}
                        </span>
                      </div>
                    )}

                    <div className="flex flex-col gap-1">
                      <span className="text-sm font-semibold text-slate-600">
                        {t("shipments.form.date")}
                      </span>
                      <span className="text-sm text-black">
                        {shipment?.date
                          ? formatDate(shipment.date, client)
                          : "--"}
                      </span>
                    </div>
                  </div>

                  <div className="w-full grid grid-cols-2 gap-4">
                    {isEditMode ? (
                      <Select
                        label={t("shipments.form.vehicle")}
                        placeholder={
                          vehiclesLoading
                            ? t("shipments.form.loadingVehicles")
                            : t("shipments.form.vehiclePlaceholder")
                        }
                        searchable
                        disabled={vehiclesLoading}
                        data={
                          vehicles
                            ?.filter((v) => v && v.rfid_tag?.rfid)
                            .map((vehicle) => ({
                              label: `${vehicle.name || "Unknown"} (${vehicle.rfid_tag.rfid})`,
                              value: vehicle.rfid_tag.rfid,
                            })) || []
                        }
                        value={form.values.vehicle_rfid || null}
                        onChange={(value) =>
                          form.setFieldValue("vehicle_rfid", value || "")
                        }
                      />
                    ) : (
                      <div className="flex flex-col gap-1">
                        <span className="text-sm font-semibold text-slate-600">
                          {t("shipments.form.vehicle")}
                        </span>
                        <div className="flex items-center gap-2">
                          {shipment?.vehicle ? (
                            <>
                              <span className="text-sm">
                                {shipment.vehicle.name}
                              </span>
                              {shipment.vehicle.rfid_tag?.rfid && (
                                <Badge
                                  color={getRandomColor(
                                    shipment.vehicle.rfid_tag.rfid
                                  )}
                                  variant="light"
                                  size="md"
                                >
                                  {shipment.vehicle.rfid_tag.rfid}
                                </Badge>
                              )}
                            </>
                          ) : (
                            <span className="text-sm">--</span>
                          )}
                        </div>
                        {shipment?.vehicle?.vehicle_model && (
                          <span className="text-xs text-gray-500">
                            {t("shipments.form.model")} {shipment.vehicle.vehicle_model.brand}{" "}
                            {shipment.vehicle.vehicle_model.model}
                          </span>
                        )}
                      </div>
                    )}

                    {isEditMode ? (
                      <Select
                        label={t("shipments.form.scanner")}
                        placeholder={t("shipments.form.scannerPlaceholder")}
                        searchable
                        clearable
                        data={devices.map((device) => ({
                          label: device.name ?? device.serial_number,
                          value: device.id.toString(),
                        }))}
                        {...form.getInputProps("scanner_id")}
                        value={form.values.scanner_id?.toString() ?? null}
                        onChange={(value) => {
                          form.setFieldValue(
                            "scanner_id",
                            value ? Number(value) : undefined
                          );
                        }}
                      />
                    ) : (
                      <div className="flex flex-col gap-1">
                        <span className="text-sm font-semibold text-slate-600">
                          {t("shipments.form.scanner")}
                        </span>
                        <span className="text-sm">
                          {shipment?.scanner?.name ?? "--"}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="w-full grid grid-cols-2 gap-4">
                    {isEditMode ? (
                      <TextInput
                        label={t("shipments.form.location")}
                        withAsterisk
                        placeholder={t("shipments.form.locationPlaceholder")}
                        {...form.getInputProps("location")}
                      />
                    ) : (
                      <div className="flex flex-col gap-1">
                        <span className="text-sm font-semibold text-slate-600">
                          {t("shipments.form.location")}
                        </span>
                        <span className="text-sm">
                          {shipment?.location ?? "--"}
                        </span>
                      </div>
                    )}

                    {isEditMode ? (
                      <TextInput
                        label={t("shipments.form.palletId")}
                        placeholder={t("shipments.form.palletIdPlaceholder")}
                        {...form.getInputProps("pallet_id")}
                      />
                    ) : (
                      <div className="flex flex-col gap-1">
                        <span className="text-sm font-semibold text-slate-600">
                          {t("shipments.form.palletId")}
                        </span>
                        <span className="text-sm">
                          {shipment?.pallet_id ?? "--"}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="w-full grid grid-cols-2 gap-4">
                    <div className="flex flex-col gap-1">
                      <span className="text-sm font-semibold text-slate-600">
                        {t("shipments.form.createdAt")}
                      </span>
                      <span className="text-sm">
                        {shipment?.createdAt
                          ? formatDateTime(shipment.createdAt, client)
                          : "--"}
                      </span>
                    </div>

                    <div className="flex flex-col gap-1">
                      <span className="text-sm font-semibold text-slate-600">
                        {t("shipments.form.lastUpdated")}
                      </span>
                      <span className="text-sm">
                        {shipment?.updatedAt
                          ? formatDateTime(shipment.updatedAt, client)
                          : "--"}
                      </span>
                    </div>
                  </div>
                </div>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="items">
              <Accordion.Control>
                {t("shipments.items")} ({displayedItems.length})
              </Accordion.Control>
              <Accordion.Panel>
                {displayedItems.length === 0 ? (
                  <div className="text-center text-gray-500 py-8">
                    {t("shipments.noItemsInShipment")}
                  </div>
                ) : (
                  <div className="w-full overflow-auto">
                    <Table striped highlightOnHover>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>{t("common.id")}</Table.Th>
                          <Table.Th>{t("items.columns.rfidTags")}</Table.Th>
                          <Table.Th>{t("common.category")}</Table.Th>
                          <Table.Th>{t("common.status")}</Table.Th>
                          <Table.Th>{t("common.location")}</Table.Th>
                          <Table.Th>{t("common.site")}</Table.Th>
                          {isEditMode && <Table.Th>{t("common.actions")}</Table.Th>}
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {displayedItems.map((item) => (
                          <Table.Tr key={item.id}>
                            <Table.Td>{item.id}</Table.Td>
                            <Table.Td>
                              {item.rfid_tags && item.rfid_tags.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {item.rfid_tags.map((tag) => (
                                    <Badge
                                      key={tag.id}
                                      color={getRandomColor(tag.rfid)}
                                      variant="light"
                                      size="sm"
                                    >
                                      {tag.rfid}
                                    </Badge>
                                  ))}
                                </div>
                              ) : (
                                "--"
                              )}
                            </Table.Td>
                            <Table.Td>
                              {item.product?.name ?? item.product?.type ?? "--"}
                            </Table.Td>
                            <Table.Td>
                              <span className="capitalize">{item.status}</span>
                            </Table.Td>
                            <Table.Td
                              className="max-w-xs truncate"
                              title={item.location}
                            >
                              {item.location ?? "--"}
                            </Table.Td>
                            <Table.Td>{item.site?.name ?? "--"}</Table.Td>
                            {isEditMode && (
                              <Table.Td className="w-20">
                                <div className="w-full flex items-center justify-center">
                                  <PermissionTooltip disabled={!canWrite()}>
                                    <ActionButton
                                      icon={IconTrash}
                                      variant="outline"
                                      color="red"
                                      onClick={() => {
                                        setItemToDelete({
                                          tag_id: item.tag_id,
                                          id: item.id,
                                        });
                                        openDeleteModal();
                                      }}
                                      disabled={!canWrite()}
                                    />
                                  </PermissionTooltip>
                                </div>
                              </Table.Td>
                            )}
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  </div>
                )}
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </form>
      </div>

      {/* Delete Item Confirmation Modal */}
      <Button.Group
        style={{ display: deleteModalOpened ? "block" : "none" }}
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
        onClick={closeDeleteModal}
      >
        <div
          className="bg-white rounded-lg p-6 max-w-md w-full mx-4"
          onClick={(e) => e.stopPropagation()}
        >
          <h3 className="text-lg font-bold mb-4">{t("shipments.removeItem.title")}</h3>
          <div className="flex flex-col gap-2 text-sm mb-6">
            <span>
              {t("shipments.removeItem.description")}
            </span>
            <div className="bg-slate-100 border border-dashed border-slate-400 rounded-lg p-4 text-center">
              <span className="font-bold">{t("shipments.removeItem.itemId")} {itemToDelete?.id}</span>
              <br />
              <span className="text-xs text-gray-600">
                {t("shipments.removeItem.tag")} {itemToDelete?.tag_id}
              </span>
            </div>
            <span className="font-bold mt-2">
              {t("shipments.removeItem.confirmation")}
            </span>
          </div>

          <div className="w-full grid grid-cols-2 gap-2">
            <Button size="sm" color="black" onClick={closeDeleteModal}>
              {t("common.cancel")}
            </Button>
            <PermissionTooltip disabled={!canWrite()}>
              <Button
                onClick={() => {
                  if (itemToDelete) {
                    handleRemoveItem(itemToDelete.tag_id);
                  }
                }}
                color="red"
                size="sm"
                leftSection={<IconTrash size={18} />}
                disabled={!canWrite()}
              >
                {t("shipments.removeItem.confirmButton")}
              </Button>
            </PermissionTooltip>
          </div>
        </div>
      </Button.Group>
    </DetailPageLayout>
  );
};

export default ShipmentLogDetailPage;
